import { useState, type CSSProperties } from "react"
import { Table } from "../../components/Table"
import { useAdminDeadLetters, useRequeueDeadLetter } from "../../hooks/useApi"
import type { DeadLetter } from "../../api/admin"

export default function DeadLettersPage() {
  const { data, isLoading, error } = useAdminDeadLetters()
  const requeue = useRequeueDeadLetter()
  const [feedback, setFeedback] = useState<string | null>(null)

  const items = data?.items ?? []

  const handleRequeue = async (letter: DeadLetter) => {
    try {
      await requeue.mutateAsync(letter.id)
      setFeedback(`Requeued job ${letter.schedule_id}.`)
    } catch (err) {
      console.error(err)
      setFeedback("Failed to requeue job.")
    }
  }

  if (isLoading) {
    return <p className="small">Loading dead letters…</p>
  }

  if (error) {
    return <p className="small">Unable to load dead letters.</p>
  }

  return (
    <div style={{ display: "grid", gap: "clamp(12px, 3vw, 20px)" }}>
      {feedback ? <div className="small" style={{ color: "var(--muted)" }}>{feedback}</div> : null}
      <Table
        data={items}
        getRowId={(row) => String(row.id)}
        columns={[
          { key: "id", title: "ID", width: "80px" },
          { key: "job_type", title: "Job" },
          {
            key: "error_message",
            title: "Error",
            render: (row) => (
              <span style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                {truncate(row.error_message, 96)}
              </span>
            ),
          },
          {
            key: "requeued_count",
            title: "Retries",
            width: "90px",
            render: (row) => <span>{row.requeued_count}</span>,
          },
          {
            key: "actions",
            title: "Actions",
            width: "140px",
            render: (row) => (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation()
                  handleRequeue(row)
                }}
                className="mono"
                style={buttonStyle}
                disabled={requeue.isPending}
              >
                Requeue
              </button>
            ),
          },
        ]}
      />
    </div>
  )
}

function truncate(value: string, max: number) {
  if (value.length <= max) return value
  return `${value.slice(0, max - 1)}…`
}

const buttonStyle: CSSProperties = {
  padding: "8px 12px",
  border: "1px solid var(--rail)",
  background: "transparent",
  color: "var(--text)",
  letterSpacing: "0.12em",
  textTransform: "uppercase" as const,
  cursor: "pointer",
  minHeight: "44px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
}
